import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmployeeCard } from "@/components/payroll/EmployeeCard";
import { usePayrollEmployees } from "@/lib/api/payroll-employees.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { PayrollEmployeeStatusFilter } from "@/lib/api/payroll-employees";

export const Route = createFileRoute("/companies/$companyId/nomina/empleados/")({
  component: EmployeesIndexPage,
});

function EmployeesIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <EmployeesIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function EmployeesIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [statusFilter, setStatusFilter] = useState<PayrollEmployeeStatusFilter>("active");
  const [q, setQ] = useState("");

  const filters = useMemo(
    () => ({ ...(q ? { q } : {}), status: statusFilter, pageSize: 50 }),
    [q, statusFilter],
  );

  const employeesQ = usePayrollEmployees(companyId, filters);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/nomina"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("payroll.employee.list.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("payroll.employee.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("payroll.employee.list.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/nomina/empleados/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("payroll.employee.list.new")}
          </Link>
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          placeholder={t("payroll.employee.list.search_placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="sm:max-w-sm"
        />
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as PayrollEmployeeStatusFilter)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t("payroll.employee.filter.active")}</SelectItem>
            <SelectItem value="archived">{t("payroll.employee.filter.archived")}</SelectItem>
            <SelectItem value="all">{t("payroll.employee.filter.all")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {employeesQ.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : employeesQ.isError ? (
        <ErrorState
          title={t("payroll.employee.list.error_title")}
          description={t("payroll.employee.list.error_body")}
          onRetry={() => void employeesQ.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : (employeesQ.data?.employees.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">
                {t("payroll.employee.list.empty_title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t("payroll.employee.list.empty_body")}
              </p>
            </div>
            <Button asChild>
              <Link
                to="/companies/$companyId/nomina/empleados/new"
                params={{ companyId }}
              >
                <Plus className="h-4 w-4" />
                {t("payroll.employee.list.new")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {employeesQ.data!.employees.map((e) => (
            <EmployeeCard key={e.id} companyId={companyId} employee={e} />
          ))}
        </div>
      )}
    </div>
  );
}
